import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TagInput } from './TagInput';
import { Search } from 'lucide-react';

interface LanguageInputProps {
  languages: string[];
  onLanguagesChange: (languages: string[]) => void;
}

// Expanded language list with more options
const languageOptions = [
  { code: 'en', name: 'English', emoji: '🇬🇧' },
  { code: 'fr', name: 'French', emoji: '🇫🇷' },
  { code: 'de', name: 'German', emoji: '🇩🇪' },
  { code: 'es', name: 'Spanish', emoji: '🇪🇸' },
  { code: 'it', name: 'Italian', emoji: '🇮🇹' },
  { code: 'pt', name: 'Portuguese', emoji: '🇵🇹' },
  { code: 'nl', name: 'Dutch', emoji: '🇳🇱' },
  { code: 'ru', name: 'Russian', emoji: '🇷🇺' },
  { code: 'ar', name: 'Arabic', emoji: '🇸🇦' },
  { code: 'zh', name: 'Mandarin Chinese', emoji: '🇨🇳' },
  { code: 'ja', name: 'Japanese', emoji: '🇯🇵' },
  { code: 'ko', name: 'Korean', emoji: '🇰🇷' },
  { code: 'hi', name: 'Hindi', emoji: '🇮🇳' },
  { code: 'bn', name: 'Bengali', emoji: '🇧🇩' },
  { code: 'tr', name: 'Turkish', emoji: '🇹🇷' },
  { code: 'vi', name: 'Vietnamese', emoji: '🇻🇳' },
  { code: 'tl', name: 'Tagalog / Filipino', emoji: '🇵🇭' },
  { code: 'fa', name: 'Persian (Farsi)', emoji: '🇮🇷' },
  { code: 'pl', name: 'Polish', emoji: '🇵🇱' },
  { code: 'th', name: 'Thai', emoji: '🇹🇭' },
  { code: 'sv', name: 'Swedish', emoji: '🇸🇪' },
  { code: 'ro', name: 'Romanian', emoji: '🇷🇴' },
  { code: 'uk', name: 'Ukrainian', emoji: '🇺🇦' },
  { code: 'cs', name: 'Czech', emoji: '🇨🇿' },
  { code: 'lb', name: 'Luxembourgish', emoji: '🇱🇺' },
  { code: 'da', name: 'Danish', emoji: '🇩🇰' },
  { code: 'fi', name: 'Finnish', emoji: '🇫🇮' },
  { code: 'no', name: 'Norwegian', emoji: '🇳🇴' },
  // Add more languages as needed
];

// Helper function to get language details by code
const getLanguageByCode = (code: string) => {
  // Normalize the code: take only the part before a hyphen (e.g., 'fr' from 'fr-FR')
  const normalizedCode = code.split('-')[0].toLowerCase();
  
  const language = languageOptions.find(lang => lang.code === normalizedCode);
  if (language) {
    return {
      code: language.code,
      name: language.name,
      emoji: language.emoji,
      display: `${language.emoji} ${language.name}`
    };
  }
  // Fallback if the normalized code still isn't found (or for generic codes)
  return {
    code: normalizedCode,
    name: code.toUpperCase(), // Show original code in fallback name
    emoji: '🌐',
    display: `🌐 ${code.toUpperCase()}`
  };
};

export const LanguageInput: React.FC<LanguageInputProps> = ({
  languages,
  onLanguagesChange
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(languageOptions);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter options based on search query
  useEffect(() => {
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      setFilteredOptions(
        languageOptions.filter(
          (lang) =>
            lang.name.toLowerCase().includes(lowerQuery) ||
            lang.code.toLowerCase().includes(lowerQuery)
        )
      );
    } else {
      // Show all options if search is empty, exclude already selected
      setFilteredOptions(languageOptions.filter(lang => !languages.includes(lang.code)));
    }
  }, [searchQuery, languages]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) && 
          inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddLanguage = (langCode: string) => {
    if (!languages.includes(langCode)) {
      onLanguagesChange([...languages, langCode]);
    }
    setSearchQuery('');
    setIsDropdownOpen(false);
  };

  const handleInputChange = (value: string) => {
    setSearchQuery(value);
    setIsDropdownOpen(true);
  };
  
  // Render tag with emoji and name
  const renderLanguageTag = (langCode: string) => {
    const lang = getLanguageByCode(langCode);
    return (
      <>
        <span className="mr-1">{lang.emoji}</span>
        {lang.name}
      </>
    );
  };

  // Handle Enter key: Add the first filtered option if available
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchQuery.trim() && filteredOptions.length > 0) {
      e.preventDefault();
      const topOption = filteredOptions[0];
      if (!languages.includes(topOption.code)) {
        handleAddLanguage(topOption.code);
      }
    }
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <TagInput
        tags={languages}
        onTagsChange={onLanguagesChange}
        placeholder={t('common.languageSearchPlaceholder', 'Type to search or select...')}
        tagClassName="bg-blue-50 text-blue-700"
        renderTag={renderLanguageTag}
        inputValue={searchQuery}
        onInputChange={handleInputChange}
        onInputKeyDown={handleInputKeyDown}
        ref={inputRef}
      />

      {isDropdownOpen && filteredOptions.length > 0 && (
        <div className="absolute left-0 right-0 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-10">
          {filteredOptions.map((lang) => (
            <button
              key={lang.code}
              className="relative w-full cursor-default select-none py-2 pl-3 pr-9 text-gray-900 hover:bg-primary-100 hover:text-primary-900 flex items-center gap-2"
              onClick={() => handleAddLanguage(lang.code)}
              type="button"
            >
              <span className="text-lg">{lang.emoji}</span>
              <span className="block truncate">{lang.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};